import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import InputModal from '../../../../../components/modals/InputModal';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext, useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useGetAllExpensesQuery,
  usePayExpensesMutation
} from '../../../../../services/endpoints/finance/expense';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { useGetCoursesSimpleQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { Expense } from '../../../../../types/finance.types';
import {
  expensesColumns,
  paidExpensesColumns
} from '../../../../../utils/consts/columnDefs/financeColumns';
import { expensesCategories, paymentMethods } from '../../../../../utils/consts/finance/finance';
import { userTypes } from '../../../../../utils/consts';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { getErrorMessage } from '../../../../../utils/utils';
import {Forbidden} from "../../../../../components/error/Forbidden";

const defaultScope = 'unpaid';

// RBAC-FE-203: Global Admin excluded from Finance per policy [H]
// Note: Cannot use user?.isAdmin because isAdmin() helper includes global_admin (user_type 3)
// Must explicitly check user_type_id to exclude Global Admin
export const userCanAdmin_Expenses = (user: User | null) =>
    !!(user?.isSuper ||
        user?.isSupremeAdmin ||
        user?.user_type_id === userTypes.admin ||
        user?.isLocationAdmin);

export const ExpensesView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const [scopes, setScopes] = useState<Scopes>({});
  const canAccessExpenses = userCanAdmin_Expenses(currentUser);
  const { data, error, isLoading, isFetching } = useGetAllExpensesQuery(filters, {
    skip: !canAccessExpenses
  });
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [payExpenses] = usePayExpensesMutation();
  const [showModal, setShowModal] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const {
    data: venues,
    error: venuesError
  } = useGetVenuesQuery({ data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } } }, {
    skip: !canAccessExpenses
  });

  const {
    data: courses,
    error: coursesError
  } = useGetCoursesSimpleQuery(undefined, { skip: !canAccessExpenses });

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          type: 'number' as const,
          label: 'Location',
          element: (
              <FormSelect
                  name="location_id"
                  label="Location"
                  options={locationOptions}
                  selected_Value={locationOptions.find(
                      (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                  )}
              />
          )
        }]
      : []),
    {
      name: 'admin_full_name',
      label: 'Admin',
      value: getFilterValue(filters.data?.filterBy, 'admin_full_name')
    },
    {
      name: 'course_id',
      type: 'number',
      element: (
          <FormSelect
              name="course_id"
              label="Session"
              options={
                  courses?.results
                      ?.map((course) => ({ value: course.id, label: course.title }))
                      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
              }
              loadingError={coursesError ? 'Error loading sessions' : undefined}
              selected_Value={courses?.results
                  ?.map((course) => ({ value: course.id, label: course.title }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id'))}
          />
      )
    },
    {
      name: 'venue_id',
      type: 'number',
      element: (
          <FormSelect
              name="venue_id"
              label="Venue"
              options={venues?.results?.map((venue) => ({ value: venue.id, label: venue.title })) ?? []}
              loadingError={venuesError ? 'Error loading venues' : undefined}
              selected_Value={venues?.results
                  ?.map((venue) => ({ value: venue.id, label: venue.title }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'venue_id'))}
          />
      )
    },
    {
      name: 'category',
      element: (
          <FormSelect
              name="category"
              label="Category"
              options={Object.entries(expensesCategories).map((p) => ({ value: p[0], label: p[1] }))}
              selected_Value={Object.entries(expensesCategories)
                  .map((p) => ({ value: p[0], label: p[1] }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'category'))}
          />
      )
    },
    {
      name: 'notes',
      label: 'Notes',
      value: getFilterValue(filters.data?.filterBy, 'notes')
    },
    {
      name: 'amount',
      label: 'Amount',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'amount')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="created_at_from"
              label="Created At"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    },
    {
      name: 'due_on_from',
      id: 'due_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="due_on_from"
              label="Due On"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'due_on', 'greater_equal_than')
    },
    {
      name: 'due_on_to',
      id: 'due_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="due_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'due_on', 'lesser_equal_than')
    },
    {
      name: 'paid_on_from',
      id: 'paid_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="paid_on_from"
              label="Paid On"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'paid_on', 'greater_equal_than')
    },
    {
      name: 'paid_on_to',
      id: 'paid_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="paid_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'paid_on', 'lesser_equal_than')
    },
    {
      name: 'invoice_number',
      label: 'Invoice Number',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'invoice_number')
    },
    {
      name: 'check_number',
      label: 'Check Number',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'check_number')
    },
    {
      name: 'payment_method',
      operator: 'equals',
      element: (
          <FormSelect
              name="payment_method"
              label="Payment Method"
              options={Object.entries(paymentMethods).map((p) => ({ value: p[0], label: p[1] }))}
              selected_Value={Object.entries(paymentMethods)
                  .map((p) => ({ value: p[0], label: p[1] }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'payment_method'))}
          />
      )
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setExpenses(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const handlePayExpenses = async (checkNumber: string) => {
    try {
      await payExpenses({
        ids: selectedIDs,
        check_number: checkNumber.trim() === '' ? undefined : +checkNumber
      }).unwrap();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  // RBAC-FE-203: Defense-in-depth guard for finance pages
  if (!canAccessExpenses) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Expenses`}
            columns={scopes[filters?.scope!] === 'Paid' ? paidExpensesColumns : expensesColumns}
            data={expenses}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="expenses"
            filters={filters?.data?.filterBy}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
            newItemEl={
              <SimpleButton
                  variant="sfc-blueberry"
                  height={40}
                  truncate={false}
                  onClick={() => navigate('/admin/finance/expenses/new')}
              >
                New Expense
              </SimpleButton>
            }
            selectableRows={filters.scope !== 'paid'}
            batchActions={[
              {
                name: 'Pay Selected',
                fn: (selectedRowsIDs: number[]) => {
                  setShowModal(true);
                  setSelectedIDs(selectedRowsIDs);
                }
              }
            ]}
        />
        <InputModal
            show={showModal}
            title="Check Number"
            message="Enter check number if paying by check. If paying by credit card, leave blank."
            placeholder="Check number"
            onClose={() => setShowModal(false)}
            onSubmit={(value) => handlePayExpenses(value)}
        />
      </div>
  );
};